import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { MoodRequestComponent } from "./mood-request/mood-request.component";
import { Author, TimelineItem, TimelineItemDialogService } from "@teammood/timeline";
import { LoggerService } from "@teammood/shared/util/logger";
import { PollRequestComponent } from "./poll-request/poll-request.component";
import { MoodRequest, PollRequest, UserRequest, UserRequestBacklog } from "./user-request.types";
import { UserRequestService } from "./user-request.service";
import { ITeam } from "@teammood/team";
import { KudosRequestComponent } from "./kudos-request/kudos-request.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { filter, takeUntil, tap } from "rxjs";
import { NgClass } from "@angular/common";
import { DaysService } from "../daily-overview/days.service";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { UserFeedbackHookComponent } from "./user-feedback-hook/user-feedback-hook.component";

@Component({
    selector: 'user-requests',
    templateUrl: './user-requests.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        TranslateModule,
        MoodRequestComponent,
        KudosRequestComponent,
        PollRequestComponent,
        UserFeedbackHookComponent
    ]
})
export class UserRequestsComponent implements OnInit, OnChanges {

    remainingPollNb: number = 0;
    userRequest: UserRequest;

    @Input() teamId: string;
    @Input() author: Author;
    @Input() teams: ITeam[];
    @Input() context: 'timeline' | 'addOtherFeedback';
    @Input() request: UserRequestBacklog;

    @Output() onCreateTimelineItemAfterRequest: EventEmitter<UserRequestEvent> = new EventEmitter<UserRequestEvent>();
    @Output() onRequestCreateQuestion: EventEmitter<void> = new EventEmitter();
    @Output() onRequestCreateIssue: EventEmitter<void> = new EventEmitter();
    @Output() onRequestCreateKudos: EventEmitter<void> = new EventEmitter();
    @Output() onRequestCreatePoll: EventEmitter<void> = new EventEmitter();

    constructor(
        private userRequestService: UserRequestService,
        private timelineItemDialogService: TimelineItemDialogService,
        private daysService: DaysService,
        private snackBarService: SnackBarService,
        private logger: LoggerService,
        private translateService: TranslateService
    ) {}

    ngOnInit() {
        if (this.request.current_request.type === 'PollRequest') {
            this.remainingPollNb = this.request.backlog_infos.byType.PollRequest;
        }
        this.userRequest = this.userRequestService.getTypedUserRequest(this.request);
    }

    ngOnChanges(changes: SimpleChanges) {
        if(changes.request?.currentValue){
            this.userRequest = this.userRequestService.getTypedUserRequest(this.request);
        }
    }
    
    onCreateMood(moodRequest: MoodRequest){
        if (moodRequest) {
            this.daysService.createMood(moodRequest.mood, this.teamId).then(async (response: any) => {

                if (response.error) {
                    this.snackBarService.error(response.error);
                } else {
                    this.snackBarService.success(this.translateService.instant('mood_created'));
                    const moodOfTheDay = response.event.item;
                    this.onCreateTimelineItemAfterRequest.emit({ type: 'moodItem', value: moodOfTheDay});
                }
            }).catch((error) => {
                this.logger.error('Error creating mood:', error);
                this.snackBarService.error(error);
            });
        }

        //this.onCreateTimelineItemAfterRequest.emit({ type: 'moodItem', value: moodValue.mood});
    }

    onRequestNextQuestion(){
        this.onCreateTimelineItemAfterRequest.emit({ type: 'next', value: null, nextRequestId: this.request.backlog_infos.nextRequestId});
    }

    async onOpenCreateKudosDialog() {
        const dialogRef = await this.timelineItemDialogService.createKudos(this.teams, this.author);
        dialogRef.beforeClosed().pipe(
            filter((kudos) => !!kudos),
            tap(kudos => {
                this.onCreateTimelineItemAfterRequest.emit({ type: 'kudosItem', value: kudos, nextRequestId: this.request.backlog_infos.nextRequestId});
            }),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    requestCreateQuestion(){
        this.onRequestCreateQuestion.emit();
    }
    requestCreateIssue(){
        this.onRequestCreateIssue.emit();
    }
    requestCreateKudos(){
        this.onRequestCreateKudos.emit();
    }
    requestCreatePoll(){
        this.onRequestCreatePoll.emit();
    }
}


export interface UserRequestEvent {
    type: 'next' | 'topicItem' | 'moodItem' | 'kudosItem' | 'questionItem';
    value: TimelineItem;
    nextRequestId?: string;
}